import React, { useMemo, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Plot from 'react-plotly.js';
import { 
  IoCheckmarkCircle, 
  IoTime, 
  IoDocumentText, 
  IoCalendar,
  IoCamera,
  IoFileTray,
  IoCloudUpload,
  IoCheckmark,
  IoClose,
  IoWarning,
  IoPencil
} from 'react-icons/io5';
import InfoButton from '@/components/InfoButton';
import { User } from '@/types';
import { API } from '@/utils/api.utils';
import api from '@/utils/api.utils';
import { useDeadlines } from '@/hooks/useDeadlines';
import { formatPriority } from '@/types/deadline';
import './NewDashboard.css';

interface University {
  university_id: number;
  user_id: number;
  status: string;
  is_suggested: boolean;
  id: number;
  university_name: string;
}

interface UniversityResponse {
  IN_PROGRESS: {
    universities: University[];
    count: number;
  };
  PENDING: {
    universities: University[];
    count: number;
  };
  COMPLETED: {
    universities: University[];
    count: number;
  };
}

interface DashboardProps {
  user?: User;
}

const Dashboard: React.FC<DashboardProps> = ({ user }) => {
  const navigate = useNavigate();
  const [showAllApplications, setShowAllApplications] = useState(false);

  // Fetch user universities
  const { data: universitiesData, isLoading } = useQuery<UniversityResponse>({
    queryKey: ['user-universities'],
    queryFn: async () => {
      const response = await api.get(API.USER_UNIVERSITIES);
      return response.data;
    },
    staleTime: 30 * 60 * 1000,
    retry: false, // Don't retry on failure
    enabled: true, // Enabled for local development
  });

  // TODO: Fetch actual data from API for these calculations
  // For now, using placeholder values that will be replaced with real data
  const profileData = {
    // Recommendations: counselor rec + teacher/mentor recs (0-10 total)
    recommendations: { counselor: 0, teachers: 0 }, // Will come from API
    
    // Personal Statement: started (true/false), outstanding AI suggestions count
    personalStatement: { started: false, outstandingSuggestions: 0 }, // Will come from drafts API
    
    // Supplements: complete count, total count, outstanding AI suggestions
    supplements: { complete: 0, total: 0, outstandingSuggestions: 0 }, // Will come from supplements API
    
    // Transcripts: junior, midyear, final (each with uploaded status and release date)
    transcripts: {
      planningToSubmit: true,
      junior: { uploaded: false, releaseDate: new Date('2025-06-01') },
      midyear: { uploaded: false, releaseDate: new Date('2026-01-15') },
      final: { uploaded: false, releaseDate: new Date('2026-06-01') },
    },
    
    // Test Scores: SAT/ACT report uploaded
    testScores: { type: 'SAT', uploaded: false }, // 'SAT', 'ACT', or null
    
    // Demonstrated Interest: registered count, total suggested count
    demonstratedInterest: { registered: 0, total: 0 }, // Will come from deadlines API
    
    // Activities: count (0-10+), outstanding AI suggestions, has tier 1
    activities: { count: 0, outstandingSuggestions: 0, hasTier1: false }, // Will come from activities API
  };

  // Calculate profile strength based on new rules
  const profileStrength = useMemo(() => {
    const weights = {
      activities: 0.10,
      testScores: 0.20,
      transcripts: 0.10,
      personalStatement: 0.20,
      supplements: 0.20,
      recommendations: 0.10,
      demonstratedInterest: 0.10,
    };

    // Recommendations: 0 recs = 0%, 1 rec = 33%, 2 recs = 67%, 3+ recs = 100%
    const totalRecs = profileData.recommendations.counselor + profileData.recommendations.teachers;
    let recommendationsProgress = 0;
    if (totalRecs === 0) recommendationsProgress = 0;
    else if (totalRecs === 1) recommendationsProgress = 33;
    else if (totalRecs === 2) recommendationsProgress = 67;
    else recommendationsProgress = 100;

    // Personal Statement: 100% if started, 0% if not, -20% for outstanding AI suggestions
    let personalStatementProgress = profileData.personalStatement.started ? 100 : 0;
    if (profileData.personalStatement.outstandingSuggestions > 0) {
      personalStatementProgress = Math.max(0, personalStatementProgress - 20);
    }

    // Supplements: % = complete/total, -20% for outstanding AI suggestions
    let supplementsProgress = 0;
    const shouldShowSupplements = profileData.supplements.total > 0;
    if (shouldShowSupplements && profileData.supplements.total > 0) {
      supplementsProgress = (profileData.supplements.complete / profileData.supplements.total) * 100;
      if (profileData.supplements.outstandingSuggestions > 0) {
        supplementsProgress = Math.max(0, supplementsProgress - 20);
      }
    }

    // Transcripts: Based on released transcripts
    let transcriptsProgress = 0;
    const shouldShowTranscripts = profileData.transcripts.planningToSubmit;
    if (shouldShowTranscripts) {
      const now = new Date();
      const releasedCount = [
        profileData.transcripts.junior,
        profileData.transcripts.midyear,
        profileData.transcripts.final,
      ].filter(t => t.releaseDate <= now).length;
      
      const uploadedCount = [
        profileData.transcripts.junior,
        profileData.transcripts.midyear,
        profileData.transcripts.final,
      ].filter(t => t.releaseDate <= now && t.uploaded).length;
      
      if (releasedCount > 0) {
        transcriptsProgress = (uploadedCount / releasedCount) * 100;
      }
    }

    // Test Scores: 0% if not uploaded, 100% if uploaded
    const testScoresProgress = profileData.testScores.uploaded ? 100 : 0;
    const shouldShowTestScores = profileData.testScores.type !== null;

    // Demonstrated Interest: % registered / total suggested
    let demonstratedInterestProgress = 0;
    if (profileData.demonstratedInterest.total > 0) {
      demonstratedInterestProgress = (profileData.demonstratedInterest.registered / profileData.demonstratedInterest.total) * 100;
    }

    // Activities: 4+ = 100%, 3 = 75%, 2 = 50%, 1 = 25%, 0 = 0%
    // -20% for outstanding AI suggestions, +20% for tier 1 (max 100%)
    let activitiesProgress = 0;
    if (profileData.activities.count >= 4) activitiesProgress = 100;
    else if (profileData.activities.count === 3) activitiesProgress = 75;
    else if (profileData.activities.count === 2) activitiesProgress = 50;
    else if (profileData.activities.count === 1) activitiesProgress = 25;
    
    if (profileData.activities.outstandingSuggestions > 0) {
      activitiesProgress = Math.max(0, activitiesProgress - 20);
    }
    if (profileData.activities.hasTier1) {
      activitiesProgress = Math.min(100, activitiesProgress + 20);
    }

    // Calculate weighted overall score
    const overall = Math.round(
      activitiesProgress * weights.activities +
      testScoresProgress * weights.testScores +
      transcriptsProgress * weights.transcripts +
      personalStatementProgress * weights.personalStatement +
      supplementsProgress * weights.supplements +
      recommendationsProgress * weights.recommendations +
      demonstratedInterestProgress * weights.demonstratedInterest
    );

    return {
      overall,
      activities: Math.round(activitiesProgress),
      testScores: Math.round(testScoresProgress),
      transcripts: Math.round(transcriptsProgress),
      personalStatement: Math.round(personalStatementProgress),
      supplements: Math.round(supplementsProgress),
      recommendations: Math.round(recommendationsProgress),
      demonstratedInterest: Math.round(demonstratedInterestProgress),
      // Visibility flags
      shouldShowSupplements,
      shouldShowTranscripts,
      shouldShowTestScores,
    };
  }, []);

  // Fetch real deadlines from API
  const { data: deadlinesData } = useDeadlines({ completed: false });
  
  // Get upcoming deadlines (next 5 incomplete deadlines sorted by due date)
  const upcomingDeadlines = useMemo(() => {
    if (!deadlinesData?.deadlines) return [];
    
    const now = new Date();
    return deadlinesData.deadlines
      .filter(d => !d.completed && new Date(d.due_date) >= now)
      .sort((a, b) => new Date(a.due_date).getTime() - new Date(b.due_date).getTime())
      .slice(0, 5)
      .map(deadline => {
        const dueDate = new Date(deadline.due_date);
        const daysLeft = Math.ceil((dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        
        return {
          id: deadline.id,
          date: dueDate.getDate().toString(),
          month: dueDate.toLocaleDateString('en-US', { month: 'short' }).toUpperCase(),
          title: deadline.title,
          description: deadline.info || `${formatPriority(deadline.priority)} Priority`,
          daysLeft,
          completion: 0, // Can be calculated based on related tasks if needed
          urgent: daysLeft <= 7,
        };
      });
  }, [deadlinesData]);

  // Fetch demonstrated interest data for all universities
  const { data: demonstratedInterestData } = useQuery({
    queryKey: ['demonstrated-interest-status'],
    queryFn: async () => {
      const response = await api.get('/api/v1/demonstrated-interest/opportunities-with-status');
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    enabled: true,
  });

  // Get application progress
  const applicationProgress = useMemo(() => {
    if (!universitiesData) return [];
    
    const allUniversities = [
      ...universitiesData.IN_PROGRESS.universities,
      ...universitiesData.PENDING.universities,
      ...universitiesData.COMPLETED.universities,
    ];

    return allUniversities.map((uni, index) => {
      // TODO: Fetch actual supplement, draft, test scores, and recommendation data from API
      // For now, using mock data to demonstrate the UI
      
      // Mock data - will be replaced with real API calls
      const hasTestScores = Math.random() > 0.5; // Will come from user profile API
      const hasPersonalStatement = Math.random() > 0.5; // Will come from drafts API
      const hasCompleteProfile = Math.random() > 0.5; // Will come from user profile API
      const hasRecommendations = Math.random() > 0.5; // Will come from recommendations API
      
      // Mock supplement data - will come from supplements API
      const totalSupplements = Math.floor(Math.random() * 5) + 1; // 1-5 supplements
      const completedSupplements = Math.floor(Math.random() * (totalSupplements + 1)); // 0 to total
      
      // Calculate demonstrated interest for this university
      const universityOpportunities = demonstratedInterestData?.filter(
        (opp: any) => opp.university_id === uni.university_id
      ) || [];
      
      const registeredCount = universityOpportunities.filter((opp: any) => opp.is_registered).length;
      const totalOpportunities = universityOpportunities.length;
      const interestDemonstrated = totalOpportunities > 0 ? registeredCount > 0 : false;
      
      // Dynamic completion calculation based on university requirements
      // Weights: Test Scores (20%), Personal Statement (20%), Profile (20%), Supplements (30%), Recommendations (10%)
      
      // Check if test scores are required (based on sat_act_status field)
      // For now, assume all universities require test scores
      const testScoresRequired = true; // Will check uni.sat_act_status !== 'test_optional'
      
      let completion = 0;
      
      // Test Scores: 20% if required
      if (testScoresRequired) {
        completion += hasTestScores ? 20 : 0;
      } else {
        // If not required, redistribute this weight to other components
        completion += 20; // Give full credit
      }
      
      // Personal Statement: 20%
      completion += hasPersonalStatement ? 20 : 0;
      
      // Complete Profile: 20%
      completion += hasCompleteProfile ? 20 : 0;
      
      // Supplements: 30% (divided evenly among supplements)
      if (totalSupplements > 0) {
        const supplementWeight = 30 / totalSupplements;
        completion += supplementWeight * completedSupplements;
      } else {
        // No supplements required, give full credit
        completion += 30;
      }
      
      // Recommendations: 10%
      completion += hasRecommendations ? 10 : 0;
      
      // Round to nearest integer
      completion = Math.round(completion);
      
      // Determine essay status based on supplements
      let essayStatus: 'not-started' | 'in-progress' | 'complete';
      if (completedSupplements === 0) {
        essayStatus = 'not-started';
      } else if (completedSupplements === totalSupplements) {
        essayStatus = 'complete';
      } else {
        essayStatus = 'in-progress';
      }
      
      // Calculate estimated time remaining using detailed logic
      let estimatedTimeRemaining = 0;
      let supplementsTime = 0;
      let personalStatementTime = 0;
      let activitiesTime = 0;
      let testScoresTime = 0;
      let profileTime = 0;
      let demonstratedInterestTime = 0;
      
      // TODO: Replace mock data with real API data
      // Mock essay data for supplements - will come from drafts/supplements API
      const essayData = Array.from({ length: totalSupplements }, (_, i) => {
        if (i < completedSupplements) {
          return { wordCount: 650, goalWordCount: 650, hasOutstandingSuggestions: false }; // Complete
        }
        const random = Math.random();
        if (random < 0.33) {
          return { wordCount: 0, goalWordCount: 650, hasOutstandingSuggestions: false }; // Not started
        } else if (random < 0.66) {
          return { wordCount: 400, goalWordCount: 650, hasOutstandingSuggestions: false }; // In progress, far from goal
        } else {
          return { wordCount: 620, goalWordCount: 650, hasOutstandingSuggestions: true }; // Near goal with suggestions
        }
      });
      
      // Mock personal statement data - will come from drafts API
      const personalStatementData = hasPersonalStatement 
        ? { wordCount: 600, goalWordCount: 650, hasOutstandingSuggestions: Math.random() > 0.5 }
        : { wordCount: 0, goalWordCount: 650, hasOutstandingSuggestions: false };
      
      // Mock activities data - will come from activities API
      const activitiesCount = Math.floor(Math.random() * 5); // 0-4 activities
      
      // Essays (supplements): 3 hours per essay not started, 2 hours if not within 100 words, 1 hour if outstanding suggestions
      essayData.forEach(essay => {
        if (essay.wordCount === 0) {
          supplementsTime += 3; // Not started
        } else if (Math.abs(essay.wordCount - essay.goalWordCount) > 100) {
          supplementsTime += 2; // More than 100 words from goal
        } else if (essay.hasOutstandingSuggestions) {
          supplementsTime += 1; // Within 100 words but has suggestions
        }
      });
      
      // Personal Statement: 8 hours not started, 4 hours if >200 words from goal, 2 hours if outstanding suggestions
      if (personalStatementData.wordCount === 0) {
        personalStatementTime = 8; // Not started
      } else if (Math.abs(personalStatementData.wordCount - personalStatementData.goalWordCount) > 200) {
        personalStatementTime = 4; // More than 200 words from goal
      } else if (personalStatementData.hasOutstandingSuggestions) {
        personalStatementTime = 2; // Has outstanding suggestions
      }
      
      // Activities: 15 min (0.25 hours) if not complete (complete = 2 or more entries)
      if (activitiesCount < 2) {
        activitiesTime = 0.25;
      }
      
      // Test Scores: 15 min (0.25 hours) if not complete (SAT/ACT selected but not uploaded)
      if (testScoresRequired && !hasTestScores) {
        testScoresTime = 0.25;
      }
      
      // Profile Info: 15 min (0.25 hours) if not complete
      if (!hasCompleteProfile) {
        profileTime = 0.25;
      }
      
      // Demonstrated Interest: 1 hour for each outstanding opportunity (not registered)
      const outstandingInterestOpportunities = totalOpportunities - registeredCount;
      demonstratedInterestTime = outstandingInterestOpportunities * 1;
      
      // Sum all components
      estimatedTimeRemaining = supplementsTime + personalStatementTime + activitiesTime + testScoresTime + profileTime + demonstratedInterestTime;
      
      // Round to 1 decimal place
      estimatedTimeRemaining = Math.round(estimatedTimeRemaining * 10) / 10;
      
      // Find the earliest deadline for this university
      const universityDeadlines = deadlinesData?.deadlines?.filter(
        (d: any) => d.university_id === uni.university_id && !d.completed
      ) || [];
      
      let deadlineText = 'No deadline set';
      if (universityDeadlines.length > 0) {
        // Sort by due_date and get the earliest
        const earliestDeadline = universityDeadlines.sort((a: any, b: any) => 
          new Date(a.due_date).getTime() - new Date(b.due_date).getTime()
        )[0];
        
        const deadlineDate = new Date(earliestDeadline.due_date);
        deadlineText = deadlineDate.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric', 
          year: 'numeric' 
        });
      }
      
      return {
        id: uni.id,
        name: uni.university_name,
        status: uni.status,
        deadline: deadlineText,
        deadlineTimestamp: universityDeadlines.length > 0 
          ? new Date(universityDeadlines[0].due_date).getTime() 
          : Infinity, // Put universities without deadlines at the end
        completion,
        supplementsComplete: completedSupplements === totalSupplements,
        totalSupplements,
        completedSupplements,
        essayStatus,
        interestDemonstrated,
        interestRegisteredCount: registeredCount,
        interestTotalCount: totalOpportunities,
        estimatedTimeRemaining,
        // Time breakdown for analytics (shared items counted once across all apps)
        timeBreakdown: {
          supplements: supplementsTime,
          personalStatement: personalStatementTime,
          activities: activitiesTime,
          testScores: testScoresTime,
          profile: profileTime,
          demonstratedInterest: demonstratedInterestTime,
        },
      };
    })
    .sort((a, b) => a.deadlineTimestamp - b.deadlineTimestamp); // Sort by earliest deadline
  }, [universitiesData, demonstratedInterestData, deadlinesData]);

  // Required documents (mock data) - memoized
  const requiredDocuments = useMemo(() => [
    {
      name: 'Official Transcript',
      uploaded: true,
      date: 'Dec 10, 2024',
      size: '2.4 MB',
    },
    {
      name: 'SAT Score Report',
      uploaded: true,
      date: 'Dec 12, 2024',
      size: '1.1 MB',
    },
    {
      name: 'ACT Score Report',
      uploaded: true,
      date: 'Dec 12, 2024',
      size: '987 KB',
    },
    {
      name: 'Mid-Year Report',
      uploaded: false,
      requiredBy: 'Feb 1, 2025',
    },
    {
      name: 'Financial Aid Documents',
      uploaded: false,
      optional: true,
    },
  ], []);

  // Analytics data - calculate total hours remaining from all applications
  // Shared items (personal statement, activities, test scores, profile) are only counted once
  // University-specific items (supplements, demonstrated interest) are summed across all universities
  const analyticsData = useMemo(() => {
    if (applicationProgress.length === 0) {
      return {
        totalHoursRemaining: '0 hrs',
        successRate: '92%',
        estimatedDecisions: 'Mar 31',
      };
    }
    
    // Sum university-specific items (supplements and demonstrated interest)
    const totalSupplementsTime = applicationProgress.reduce((sum, app) => sum + app.timeBreakdown.supplements, 0);
    const totalDemonstratedInterestTime = applicationProgress.reduce((sum, app) => sum + app.timeBreakdown.demonstratedInterest, 0);
    
    // Take max of shared items (only count once, not per university)
    const personalStatementTime = Math.max(...applicationProgress.map(app => app.timeBreakdown.personalStatement));
    const activitiesTime = Math.max(...applicationProgress.map(app => app.timeBreakdown.activities));
    const testScoresTime = Math.max(...applicationProgress.map(app => app.timeBreakdown.testScores));
    const profileTime = Math.max(...applicationProgress.map(app => app.timeBreakdown.profile));
    
    // Calculate total
    const totalHours = totalSupplementsTime + totalDemonstratedInterestTime + personalStatementTime + activitiesTime + testScoresTime + profileTime;
    const roundedTotal = Math.round(totalHours * 10) / 10;
    
    const formattedHours = roundedTotal % 1 === 0 
      ? `${roundedTotal} hrs` 
      : `${roundedTotal.toFixed(1)} hrs`;
    
    // Find next college application deadline
    const now = new Date();
    const collegeAppDeadlines = deadlinesData?.deadlines?.filter(
      (d: any) => d.source_type === 'college_application' && !d.completed && new Date(d.due_date) >= now
    ) || [];
    
    const nextDeadline = collegeAppDeadlines.sort((a: any, b: any) => 
      new Date(a.due_date).getTime() - new Date(b.due_date).getTime()
    )[0];
    
    let nextDeadlineDate = 'No Apps Due';
    let nextDeadlineTitle = '';
    
    if (nextDeadline) {
      const deadlineDate = new Date(nextDeadline.due_date);
      nextDeadlineDate = deadlineDate.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric'
      });
      nextDeadlineTitle = nextDeadline.title || '';
    }
    
    return {
      totalHoursRemaining: formattedHours,
      successRate: '92%',
      nextDeadlineDate,
      nextDeadlineTitle,
    };
  }, [applicationProgress, deadlinesData]);

  // Memoize chart data to prevent re-renders
  const monthlyProgressChartData = useMemo(() => [
    {
      type: 'scatter',
      mode: 'lines',
      name: 'Applications Started',
      x: ['Sep', 'Oct', 'Nov', 'Dec', 'Jan'],
      y: [2, 4, 6, 7, 8],
      line: { color: '#6B7AE8', width: 3 },
      fill: 'tozeroy',
      fillcolor: 'rgba(107, 122, 232, 0.1)'
    } as any,
    {
      type: 'scatter',
      mode: 'lines',
      name: 'Applications Submitted',
      x: ['Sep', 'Oct', 'Nov', 'Dec', 'Jan'],
      y: [0, 1, 2, 4, 5],
      line: { color: '#10B981', width: 3 }
    } as any
  ], []);

  const monthlyProgressChartLayout = useMemo(() => ({
    xaxis: { title: { text: 'Month' } },
    yaxis: { title: { text: 'Number of Applications' } },
    margin: { t: 20, r: 20, b: 60, l: 60 },
    plot_bgcolor: '#ffffff',
    paper_bgcolor: '#ffffff',
    showlegend: true,
    legend: { x: 0, y: 1.1, orientation: 'h' },
    height: 400
  } as any), []);

  const schoolTypeChartData = useMemo(() => [
    {
      type: 'pie',
      labels: ['Reach Schools', 'Target Schools', 'Safety Schools'],
      values: [3, 3, 2],
      marker: {
        colors: ['#EF4444', '#6B7AE8', '#10B981']
      },
      textinfo: 'label+percent',
      textposition: 'inside'
    } as any
  ], []);

  const schoolTypeChartLayout = useMemo(() => ({
    margin: { t: 20, r: 20, b: 20, l: 20 },
    plot_bgcolor: '#ffffff',
    paper_bgcolor: '#ffffff',
    showlegend: true,
    legend: { x: 0, y: -0.1, orientation: 'h' },
    height: 400
  } as any), []);

  const plotConfig = useMemo(() => ({
    responsive: true,
    displayModeBar: false,
    displaylogo: false
  }), []);

  const getStatusBadgeColor = useCallback((status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'bg-green-100 text-green-700';
      case 'IN_PROGRESS':
        return 'bg-yellow-100 text-yellow-700';
      case 'PENDING':
        return 'bg-blue-100 text-blue-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  }, []);

  const getStatusLabel = useCallback((status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'Submitted';
      case 'IN_PROGRESS':
        return 'In Progress';
      case 'PENDING':
        return 'Under Review';
      default:
        return status;
    }
  }, []);

  return (
    <div className="new-dashboard dark:bg-gray-900">
      <div className="dashboard-container dark:bg-gray-900">
        {/* Profile Strength Section */}
        <div className="dashboard-grid">
          <div className="profile-strength-card dark:bg-gray-800 dark:border-gray-700">
            <div className="card-header">
              <h2 className="card-title dark:text-gray-100">
                Profile Strength
                <InfoButton elementId="dashboard-profile-strength" />
              </h2>
              <span className="profile-strength-percentage dark:text-blue-400">{profileStrength.overall}%</span>
            </div>

            <div className="profile-section">
              <div className="profile-avatar-container">
                <img
                  alt="Profile"
                  className="profile-avatar"
                  src={user?.photoURL || 'https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-1.jpg'}
                />
                <button className="profile-camera-button dark:bg-blue-600 dark:hover:bg-blue-700">
                  <IoCamera className="camera-icon" />
                </button>
              </div>
              <h3 className="profile-name dark:text-gray-100">{user?.displayName || user?.name || 'User'}</h3>
              <p className="profile-class dark:text-gray-300">
                {user?.graduating_year ? `Class of ${user.graduating_year}` : 'Class of 2026'}
              </p>
              <p className="profile-school dark:text-gray-400">
                {user?.high_school || 'Add your school'}
              </p>
            </div>

            <div className="strength-bars">
              {/* Profile - Always show */}
              <div className="strength-bar-item">
                <span className="strength-label dark:text-gray-300">Profile</span>
                <div className="strength-bar-wrapper">
                  <div className="strength-bar-bg dark:bg-gray-700">
                    <div 
                      className={`strength-bar-fill ${
                        profileStrength.activities >= 75 ? 'bg-green-500' : 
                        profileStrength.activities >= 50 ? 'bg-yellow-500' : 
                        'bg-red-500'
                      }`} 
                      style={{ width: `${profileStrength.activities}%` }}
                    ></div>
                  </div>
                  <span className={`strength-percentage ${
                    profileStrength.activities >= 75 ? 'text-green-600 dark:text-green-400' : 
                    profileStrength.activities >= 50 ? 'text-yellow-600 dark:text-yellow-400' : 
                    'text-red-600 dark:text-red-400'
                  }`}>{profileStrength.activities}%</span>
                </div>
              </div>

              {/* Test Scores - Show if type is selected */}
              {profileStrength.shouldShowTestScores && (
                <div className="strength-bar-item">
                  <span className="strength-label dark:text-gray-300">Test Scores</span>
                  <div className="strength-bar-wrapper">
                    <div className="strength-bar-bg dark:bg-gray-700">
                      <div 
                        className={`strength-bar-fill ${profileStrength.testScores === 100 ? 'bg-green-500' : 'bg-red-500'}`} 
                        style={{ width: `${profileStrength.testScores}%` }}
                      ></div>
                    </div>
                    <span className={`strength-percentage ${
                      profileStrength.testScores === 100 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                    }`}>{profileStrength.testScores}%</span>
                  </div>
                </div>
              )}

              {/* Transcripts - Show if planning to submit */}
              {profileStrength.shouldShowTranscripts && (
                <div className="strength-bar-item">
                  <span className="strength-label dark:text-gray-300">Transcripts</span>
                  <div className="strength-bar-wrapper">
                    <div className="strength-bar-bg dark:bg-gray-700">
                      <div 
                        className={`strength-bar-fill ${
                          profileStrength.transcripts >= 75 ? 'bg-green-500' : 
                          profileStrength.transcripts >= 50 ? 'bg-yellow-500' : 
                          'bg-red-500'
                        }`} 
                        style={{ width: `${profileStrength.transcripts}%` }}
                      ></div>
                    </div>
                    <span className={`strength-percentage ${
                      profileStrength.transcripts >= 75 ? 'text-green-600 dark:text-green-400' : 
                      profileStrength.transcripts >= 50 ? 'text-yellow-600 dark:text-yellow-400' : 
                      'text-red-600 dark:text-red-400'
                    }`}>{profileStrength.transcripts}%</span>
                  </div>
                </div>
              )}

              {/* Personal Statement - Always show */}
              <div className="strength-bar-item">
                <span className="strength-label dark:text-gray-300">Personal Statement</span>
                <div className="strength-bar-wrapper">
                  <div className="strength-bar-bg dark:bg-gray-700">
                    <div 
                      className={`strength-bar-fill ${
                        profileStrength.personalStatement >= 75 ? 'bg-green-500' : 
                        profileStrength.personalStatement >= 50 ? 'bg-yellow-500' : 
                        'bg-red-500'
                      }`} 
                      style={{ width: `${profileStrength.personalStatement}%` }}
                    ></div>
                  </div>
                  <span className={`strength-percentage ${
                    profileStrength.personalStatement >= 75 ? 'text-green-600 dark:text-green-400' : 
                    profileStrength.personalStatement >= 50 ? 'text-yellow-600 dark:text-yellow-400' : 
                    'text-red-600 dark:text-red-400'
                  }`}>{profileStrength.personalStatement}%</span>
                </div>
              </div>

              {/* Supplements - Always show */}
              <div className="strength-bar-item">
                <span className="strength-label dark:text-gray-300">Supplements</span>
                <div className="strength-bar-wrapper">
                  <div className="strength-bar-bg dark:bg-gray-700">
                    <div 
                      className={`strength-bar-fill ${
                        profileStrength.supplements >= 75 ? 'bg-green-500' : 
                        profileStrength.supplements >= 50 ? 'bg-yellow-500' : 
                        'bg-red-500'
                      }`} 
                      style={{ width: `${profileStrength.supplements}%` }}
                    ></div>
                  </div>
                  <span className={`strength-percentage ${
                    profileStrength.supplements >= 75 ? 'text-green-600 dark:text-green-400' : 
                    profileStrength.supplements >= 50 ? 'text-yellow-600 dark:text-yellow-400' : 
                    'text-red-600 dark:text-red-400'
                  }`}>{profileStrength.supplements}%</span>
                </div>
              </div>

              {/* Recommendations - Always show */}
              <div className="strength-bar-item">
                <span className="strength-label dark:text-gray-300">Recommendations</span>
                <div className="strength-bar-wrapper">
                  <div className="strength-bar-bg dark:bg-gray-700">
                    <div 
                      className={`strength-bar-fill ${
                        profileStrength.recommendations >= 75 ? 'bg-green-500' : 
                        profileStrength.recommendations >= 50 ? 'bg-yellow-500' : 
                        'bg-red-500'
                      }`} 
                      style={{ width: `${profileStrength.recommendations}%` }}
                    ></div>
                  </div>
                  <span className={`strength-percentage ${
                    profileStrength.recommendations >= 75 ? 'text-green-600 dark:text-green-400' : 
                    profileStrength.recommendations >= 50 ? 'text-yellow-600 dark:text-yellow-400' : 
                    'text-red-600 dark:text-red-400'
                  }`}>{profileStrength.recommendations}%</span>
                </div>
              </div>

              {/* Demonstrated Interest - Always show */}
              <div className="strength-bar-item">
                <span className="strength-label dark:text-gray-300">Demonstrated Interest</span>
                <div className="strength-bar-wrapper">
                  <div className="strength-bar-bg dark:bg-gray-700">
                    <div 
                      className={`strength-bar-fill ${
                        profileStrength.demonstratedInterest >= 75 ? 'bg-green-500' : 
                        profileStrength.demonstratedInterest >= 50 ? 'bg-yellow-500' : 
                        'bg-red-500'
                      }`} 
                      style={{ width: `${profileStrength.demonstratedInterest}%` }}
                    ></div>
                  </div>
                  <span className={`strength-percentage ${
                    profileStrength.demonstratedInterest >= 75 ? 'text-green-600 dark:text-green-400' : 
                    profileStrength.demonstratedInterest >= 50 ? 'text-yellow-600 dark:text-yellow-400' : 
                    'text-red-600 dark:text-red-400'
                  }`}>{profileStrength.demonstratedInterest}%</span>
                </div>
              </div>
            </div>

            <button 
              className="complete-profile-button dark:bg-blue-600 dark:hover:bg-blue-700"
              onClick={() => navigate('/settings')}
            >
              Complete Profile
            </button>

            {/* Required Documents Section (replacing Quick Links) */}
            <div className="documents-section dark:border-gray-700">
              <h4 className="documents-title dark:text-gray-200">
                Required Documents
                <InfoButton elementId="dashboard-required-documents" />
              </h4>
              <div className="documents-list">
                {requiredDocuments.map((doc, index) => (
                  <div
                    key={index}
                    className={`document-item ${doc.uploaded ? 'document-uploaded dark:bg-green-900/20 dark:border-green-700' : doc.optional ? 'document-optional dark:bg-gray-700 dark:border-gray-600' : 'document-pending dark:bg-yellow-900/20 dark:border-yellow-700'}`}
                  >
                    <div className="document-icon-container">
                      <IoFileTray className="document-icon" />
                    </div>
                    <div className="document-info">
                      <h3 className="document-name dark:text-gray-100">{doc.name}</h3>
                      <p className="document-meta dark:text-gray-400">
                        {doc.uploaded ? `${doc.date} • ${doc.size}` : doc.requiredBy ? `Required by ${doc.requiredBy}` : 'Optional'}
                      </p>
                    </div>
                    {doc.uploaded ? (
                      <IoCheckmarkCircle className="document-check-icon dark:text-green-400" />
                    ) : (
                      <button className="document-upload-button dark:text-blue-400 dark:hover:text-blue-300">Upload</button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="main-content-area">
            {/* Upcoming Deadlines */}
            <div className="deadlines-card dark:bg-gray-800 dark:border-gray-700">
              <div className="card-header">
                <h2 className="card-title dark:text-gray-100">
                  Upcoming Deadlines
                  <InfoButton elementId="dashboard-upcoming-deadlines" />
                </h2>
                <button 
                  onClick={() => navigate('/deadlines')} 
                  className="view-all-link dark:text-blue-400 dark:hover:text-blue-300"
                >
                  View All
                </button>
              </div>

              <div className="deadlines-list">
                {upcomingDeadlines.length === 0 ? (
                  <div className="empty-state dark:text-gray-400">
                    <IoCalendar className="empty-icon" />
                    <p>No upcoming deadlines</p>
                    <button 
                      onClick={() => navigate('/deadlines')} 
                      className="empty-action-btn dark:bg-blue-600 dark:hover:bg-blue-700"
                    >
                      Add Deadline
                    </button>
                  </div>
                ) : (
                  upcomingDeadlines.map((deadline) => (
                    <div
                      key={deadline.id}
                      className={`deadline-item ${deadline.urgent ? 'deadline-urgent dark:bg-red-900/20 dark:border-red-700' : 'deadline-normal dark:bg-yellow-900/20 dark:border-yellow-700'}`}
                    >
                      <div className={`deadline-date-box ${deadline.urgent ? 'bg-red-500' : 'bg-yellow-500'}`}>
                        <span className="deadline-date">{deadline.date}</span>
                        <span className="deadline-month">{deadline.month}</span>
                      </div>
                      <div className="deadline-content">
                        <h3 className="deadline-title dark:text-gray-100">{deadline.title}</h3>
                        <div className="deadline-meta">
                          <span className={`deadline-badge ${deadline.urgent ? 'badge-red dark:bg-red-900/40 dark:text-red-300' : 'badge-yellow dark:bg-yellow-900/40 dark:text-yellow-300'}`}>
                            {deadline.daysLeft} {deadline.daysLeft === 1 ? 'day' : 'days'} left
                          </span>
                        </div>
                      </div>
                      <button 
                        onClick={() => navigate('/deadlines')} 
                        className="deadline-edit-button dark:text-gray-400 dark:hover:text-gray-200"
                        title="Edit deadline"
                      >
                        <IoPencil size={18} />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Application Progress */}
            <div className="application-progress-card dark:bg-gray-800 dark:border-gray-700">
              <div className="card-header">
                <h2 className="card-title dark:text-gray-100">
                  Application Progress
                  <InfoButton elementId="dashboard-application-progress" />
                </h2>
                <button 
                  className="view-all-link dark:text-blue-400 dark:hover:text-blue-300"
                  onClick={() => navigate('/applications')}
                >
                  View All →
                </button>
              </div>

              <div className="applications-list">
                {isLoading ? (
                  <div className="loading-state dark:text-gray-400">Loading applications...</div>
                ) : applicationProgress.length > 0 ? (
                  <>
                    {(showAllApplications ? applicationProgress : applicationProgress.slice(0, 4)).map((app) => (
                      <div key={app.id} className="application-item dark:border-gray-700 dark:hover:bg-gray-700/50">
                        <div className="application-header">
                          <div className="application-info">
                            <div className="application-icon dark:bg-blue-900/30">
                              <IoDocumentText className="application-icon-svg dark:text-blue-400" />
                            </div>
                            <div>
                              <h3 className="application-name dark:text-gray-100">{app.name}</h3>
                              <p className="application-deadline dark:text-gray-400">Regular Decision • Deadline: {app.deadline}</p>
                            </div>
                          </div>
                          <span className={`status-badge ${getStatusBadgeColor(app.status)} dark:bg-opacity-20`}>
                            {getStatusLabel(app.status)}
                          </span>
                        </div>
                        <div className="application-progress-bar">
                          <div className="progress-bar-bg dark:bg-gray-700">
                            <div
                              className="progress-bar-fill dark:bg-blue-500"
                              style={{ width: `${app.completion}%` }}
                            ></div>
                          </div>
                          <span className="progress-percentage dark:text-gray-300">{app.completion}%</span>
                        </div>
                        <div className="application-status-icons dark:text-gray-300">
                          {/* Supplements Status */}
                          <span className="status-icon-item">
                            {app.supplementsComplete ? (
                              <>
                                <IoCheckmarkCircle className="status-icon-check dark:text-green-400" />
                                Supplements Complete
                              </>
                            ) : app.essayStatus === 'in-progress' ? (
                              <>
                                <IoWarning className="status-icon-warning dark:text-orange-400" />
                                Essays In Progress
                              </>
                            ) : (
                              <>
                                <IoClose className="status-icon-error dark:text-red-400" />
                                Essays Not Started
                              </>
                            )}
                          </span>
                          
                          {/* Demonstrated Interest */}
                          <span className="status-icon-item">
                            {app.interestTotalCount === 0 ? (
                              <>
                                <IoClose className="status-icon-not-considered dark:text-gray-400" />
                                Not Considered
                              </>
                            ) : app.interestRegisteredCount > 0 ? (
                              <>
                                <IoCheckmarkCircle className="status-icon-check dark:text-green-400" />
                                Interest Demonstrated
                              </>
                            ) : (
                              <>
                                <IoClose className="status-icon-error dark:text-red-400" />
                                Interest Not Demonstrated
                              </>
                            )}
                          </span>
                          
                          {/* Estimated Time Remaining */}
                          <span className="status-icon-item">
                            <IoTime className="status-icon-time dark:text-blue-400" />
                            Est. Time Remaining: {app.estimatedTimeRemaining}h
                          </span>
                        </div>
                      </div>
                    ))}
                    
                    {/* Show All / Show Less Toggle */}
                    {applicationProgress.length > 4 && (
                      <button
                        onClick={() => setShowAllApplications(!showAllApplications)}
                        className="show-all-button dark:text-blue-400 dark:hover:text-blue-300"
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: '8px 0',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: '500',
                          textAlign: 'center',
                          width: '100%',
                          marginTop: '8px'
                        }}
                      >
                        {showAllApplications ? 'Show Less' : `Show All (${applicationProgress.length})`}
                      </button>
                    )}
                  </>
                ) : (
                  <div className="empty-state dark:text-gray-400">No applications yet. Start by adding a university to your list.</div>
                )}
              </div>

              <button 
                className="add-application-button dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:border-blue-500 dark:hover:text-blue-400"
                onClick={() => navigate('/college-list-builder')}
              >
                <span>+</span> Add New Application
              </button>
            </div>
          </div>
        </div>

        {/* Application Analytics */}
        <div className="analytics-section">
          <div className="analytics-card dark:bg-gray-800 dark:border-gray-700">
            <h2 className="card-title dark:text-gray-100">
              Application Analytics
              <InfoButton elementId="dashboard-application-analytics" />
            </h2>

            <div className="analytics-charts-container">
              <div className="chart-wrapper dark:bg-gray-900 dark:border-gray-700">
                <h3 className="chart-title dark:text-gray-200">Monthly Progress Overview</h3>
                <Plot
                  data={monthlyProgressChartData}
                  layout={monthlyProgressChartLayout}
                  config={plotConfig}
                  style={{ width: '100%', height: '400px' }}
                  useResizeHandler={false}
                />
              </div>

              <div className="chart-wrapper dark:bg-gray-900 dark:border-gray-700">
                <h3 className="chart-title dark:text-gray-200">Applications by School Type</h3>
                <Plot
                  data={schoolTypeChartData}
                  layout={schoolTypeChartLayout}
                  config={plotConfig}
                  style={{ width: '100%', height: '400px' }}
                  useResizeHandler={false}
                />
              </div>
            </div>

            <div className="analytics-grid">
              <div className="analytics-stat dark:bg-gray-900 dark:border-gray-700">
                <div className="stat-header">
                  <span className="stat-label dark:text-gray-300">Total Hours Remaining</span>
                  <IoTime className="stat-icon dark:text-gray-500" />
                </div>
                <p className="stat-value dark:text-gray-100">{analyticsData.totalHoursRemaining}</p>
                <p className="stat-subtext dark:text-gray-400">Across all applications</p>
              </div>

              <div className="analytics-stat dark:bg-gray-900 dark:border-gray-700">
                <div className="stat-header">
                  <span className="stat-label dark:text-gray-300">Success Rate</span>
                  <IoCheckmarkCircle className="stat-icon dark:text-gray-500" />
                </div>
                <p className="stat-value dark:text-gray-100">{analyticsData.successRate}</p>
                <p className="stat-subtext dark:text-gray-400">Profile completeness</p>
              </div>

              <div className="analytics-stat dark:bg-gray-900 dark:border-gray-700">
                <div className="stat-header">
                  <span className="stat-label dark:text-gray-300">Next Deadline</span>
                  <IoCalendar className="stat-icon dark:text-gray-500" />
                </div>
                <p className="stat-value dark:text-gray-100">{analyticsData.nextDeadlineDate}</p>
                <p className="stat-subtext dark:text-gray-400" style={{ 
                  overflow: 'hidden', 
                  textOverflow: 'ellipsis', 
                  whiteSpace: 'nowrap' 
                }}>
                  {analyticsData.nextDeadlineTitle || 'College application deadline'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
